<script lang="ts" module>
  export interface PriceFilterProps {
    min: number;
    max: number;
    value?: number[];
    onValueChange?: (value: number[]) => void;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { CaretDown } from 'phosphor-svelte';
  import { css } from '@agapi/ui/panda/css';
  import { Popover, PopoverTrigger, PopoverContent, PopoverDescription, PopoverPositioner } from '@agapi/ui/popover';
  import { selectStyles } from '@agapi/ui/select';
  import { RangeSlider } from '@agapi/ui/range-slider';

  import { getLocaleFromUrl } from '../../utils';
  import { formatCurrency } from '../../utils/pricing.server';
  import * as m from '$lib/parachute/messages';
  import { Badge } from '@agapi/ui/badge';
  import { transformToCurrency } from '../../utils/locale.shared';

  let { min, max, value, onValueChange }: PriceFilterProps = $props();
  let clearState = $state(`${min === max ? Math.max(min - 1, 0) : min}${max}`);
  let seed = $state(0);

  const classes = selectStyles.raw();

  const { currency } = getLocaleFromUrl(page.url);

  const format = formatCurrency(currency);

  const setPriceRangeLabel = (details: { value: number[] }) => {
    return `${transformToCurrency(details.value[0], getLocaleFromUrl(page.url))} - ${transformToCurrency(details.value[1], getLocaleFromUrl(page.url))}`;
  }

  let hasChange = $derived(value?.[0] !== min || value?.[1] !== max);
  let priceRange = $state(setPriceRangeLabel({ value: [min, max] }));

  const handleValueChange = (details: { value: number[] }) => {
    hasChange = details.value[0] !== min || details.value[1] !== max;
    if (hasChange && details.value.length === 2) {
      priceRange = setPriceRangeLabel(details);
    }
    onValueChange?.(hasChange ? details.value : []);
  }

  $effect(() => {
    clearState = `${min === max ? Math.max(min - 1, 0) : min}${max}`;
  });

  export const refresh = () => {
    hasChange = value?.[0] !== min || value?.[1] !== max;
    seed += 1;
  }

  let rangeSliderRef = $state<any>();

  export const setValue = ({ max, min }: { max: number; min: number}) => {
    const value = [min / 100, max / 100];
    rangeSliderRef?.setValue(value);
  }

</script>

<Popover positioning={{ placement: 'bottom-start' }}>
  <PopoverTrigger css={{...classes.trigger, fontWeight: 'bold', '[data-state="open"] &': { borderColor: 'forms.action' }}}>
    {m.price()}

    {#if hasChange}
      <Badge size="sm">{priceRange}</Badge>
    {/if}

    <CaretDown class={css(classes.indicator)}>
      <title>{m.iconDropdown()}</title>
    </CaretDown>
  </PopoverTrigger>

  <PopoverPositioner>
    <PopoverContent css={{ ...classes.content, width: 'containers.xs' }}>
      <PopoverDescription css={{ p: 4 }}>
        {#key seed + clearState}
          <RangeSlider
            bind:this={rangeSliderRef}
            id="price-range"
            label={m.priceRange()}
            min={min === max ? Math.max(min - 1, 0) : min}
            max={max}
            step={1}
            value={Array.isArray(value) ? [value[0] === max ? Math.max(value[0] - 1, 0) : value[0], value[1]] : undefined}
            unit={format.unit}
            unitPosition={format.unitPosition}
            onValueChange={handleValueChange}
          />
        {/key}
      </PopoverDescription>
    </PopoverContent>
  </PopoverPositioner>
</Popover>
